<template>
  <div class="interactive-video-exam-player">
    <div class="video-container">
      <!-- Loading  -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p> {{ $t('general.loading') }} ...</p>
        </div>
      </div>

      <!-- Video -->
      <div class="time-display">
        <div class="current_time">{{ formatTime(currentVideoTime) }}</div>
        <div class="total_time">{{ formatTime(totalDuration) }}</div>
      </div>
      <video
          id="interactive-video-player"
          class="video-js vjs-default-skin vjs-big-play-centered"
          controls
          data-setup="{}"
          height="400"
          playsinline
          preload="metadata"
          width="100%"
      >
        <p class="vjs-no-js">
          Video oynatmak için JavaScript etkin olmalıdır.
          <a :href="selected_content.file ? selected_content.file.file_link : '#'" target="_blank">
            Videoyu doğrudan izle
          </a>
        </p>
      </video>

      <!-- Question & Feedback Overlay -->
      <QuestionFeedbackOverlay
          v-if="currentQuestion"
          ref="questionOverlayRef"
          :apiResult="apiResult"
          :question="currentQuestion"
          @pause-video="pauseVideo"
          @play-video="playVideo"
          @continue-video="continueToVideo"
          @answer-the-selected-question="answerTheSelectedQuestion"
      />
    </div>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import 'videojs-contrib-hls';
import 'videojs-markers';
import 'videojs-markers/dist/videojs.markers.css';
import { GET_ITEM_DETAIL_BY_ID, UPDATE_ITEM_BY_ID } from "@/core/services/store/REST.module";
import {
  disableTimelineControls,
  getVideoType,
  timeToSeconds,
  formatTime,
  setupPageVisibility,
  cleanupPageVisibility,
  addQuestionMarkers,
  clearQuestionMarkers,
  adjustVideoSize
} from "@/utils/videojsUtils";
import { showError, showInfo } from "@/utils/alertUtils.js";
import QuestionFeedbackOverlay from './QuestionFeedbackOverlay.vue';

export default {
  name: "interactive-video-exam-player",
  components: {
    QuestionFeedbackOverlay
  },
  props: {
    selected_content: {
      type: Object,
      required: true
    },
    item_content: {
      type: Object,
      required: true
    },
    hashedCode: {
      type: String,
      default: ''
    },
    pauseOnBackground: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      player: null,
      isLoading: false,
      updateContentData: {},
      currentDuration: 0, // Kullanıcının kaldığı pozisyon
      shouldSeekBack: false,
      backTime: null,
      questions: [], // Tüm sorular
      currentVideoTime: 0, // Mevcut video süresi
      totalDuration: 0, // Toplam video süresi
      currentQuestion: null, // Şu anda gösterilecek soru
      lastAnsweredQuestionTime: null, // Son cevaplanan sorunun zamanı
      answeredQuestions: [], // Cevaplanmış soruların ID'leri
      apiResult: null, // Soru cevaplandıktan sonra dönen result
      intersectionObserver: null,
      pageVisibilityHandler: null,
      // API durumları
      questionsLoaded: false,
      positionLoaded: false,
      isContentCompleted: false, // Video daha önce tamamlanmışsa true
    };
  },
  mounted() {
    this.getExamContent();

    this.disableEduLock()
    this.$nextTick(() => {
      this.initVideoPlayer();

      // video tabı değişirse , video durdurulsun
      if (this.pauseOnBackground) {
        setupPageVisibility(this.player, this);
      }
    });
  },
  beforeDestroy() {
    // Marker'ları temizle
    if (this.player) {
      clearQuestionMarkers(this.player);
      this.player.dispose();
    }

    // Clean setupPageVisibility
    cleanupPageVisibility(this);
  },

  watch: {
    currentQuestion(newQuestion) {
      if (newQuestion && this.player && this.player.isFullscreen()) {
        // Modal açıldığında fullscreen durumunu kontrol et
        this.$nextTick(() => {
          this.handleFullscreenChange();
        });
      }
    }
  },

  methods: {
    formatTime,
    disableEduLock() {
      // window a set edilen bu Fn'ler , video ya müdahaale ediyor, diğer video componentlerinde kullanıma dikkat edilmeli
      const global = window;

      if (typeof global.edulock === 'function') {
        // etkisizleştirilmiş Fn
        global.edulock = () => {
          console.warn('[EduLock] Engellendi: interactive video sırasında devre dışı bırakıldı.');
        };
      }
    },

    initVideoPlayer() {
      // iOS kontrolü
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      this.player = videojs('interactive-video-player', {
        controls: true,
        fluid: false, // fluid'i false yapıyoruz - manuel kontrol için
        preload: 'metadata',
        playsinline: true,//ios da kendi kodlarımızın etkin olması için kulanılır
        language: 'tr',
        controlBar: {
          pictureInPictureToggle: false,
          playbackRateMenuButton: false, // iOS'ta playback rate butonunu gizle
          fullscreenToggle: !isIOS, // iOS'ta fullscreen butonunu gizle
        },
        plugins: {
          markers: {
            markers: []
          }
        },
        playbackRates: [1],  // Hız seçenekleri

      }, () => {
        this.setupVideoSource();
        this.setupVideoEvents();
      });
    },

    setupVideoSource() {
      if (!this.selected_content?.file?.file_link) {
        console.warn('Video kaynağı bulunamadı');
        return;
      }

      const videoUrl = this.selected_content.file.file_link;

      this.player.src({
        src: videoUrl,
        type: getVideoType(videoUrl)
      });

      this.player.load();
    },

    setupVideoEvents() {
      this.player.on('loadstart', () => {
        this.isLoading = true;
        if (this.player && this.player.el()) {
          this.player.el().classList.add('vjs-loading');
        }

      });

      this.player.on('loadedmetadata', () => {
        // Toplam video süresi
        this.totalDuration = this.player.duration();

        // Aspect ratio'ya göre video boyutlandırma
        adjustVideoSize(this.player, this.$el, 'interactive-video');

        // Video metadata yüklendiğinde kullanıcının kaldığı yeri al
        this.getVideoWhereIStay(this.selected_content.id);

        // Marker'ları yeniden yükle (eğer sorular varsa)
        if (this.questions && this.questions.length > 0) {
          setTimeout(() => {
            addQuestionMarkers(this.player, this.questions);
          }, 500);
        }
      });

      this.player.on('canplay', () => {
        //Video oynatılabilir durumda
      });

      this.player.on('loadeddata', () => {
        // Video verisi yüklendiğinde pozisyonu ayarla
        if (this.currentDuration > 0) {
          this.setVideoPosition(this.currentDuration);
        }
      });

      this.player.on('error', () => {
        this.isLoading = false;
        this.handleVideoError();
      });

      // Big play button kontrolü
      this.player.on('pause', () => {
        // Video duraklatıldığında pozisyonu güncelle
        this.updateVideoWhereStay();
      });

      this.player.on('play', () => {
        //Video oynatılıyor
      });

      this.player.on('ended', () => {
        // Video bittiğinde complete-content API'sine istek
        this.$emit('complete-content', this.selected_content);
      });

      // Fullscreen event'lerini dinle
      this.player.on('fullscreenchange', () => {
        this.handleFullscreenChange();
      });

      // saniyede 3 defa tetiklenir
      this.player.on('timeupdate', () => {
        this.currentVideoTime = this.player.currentTime();
        this.checkQuestionTiming(this.currentVideoTime);
      });
    },

    handleVideoError() {
      this.isLoading = false;
      showError("Video oynatılamadı. Lütfen bağlantınızı kontrol edip tekrar deneyin.");
    },

    // Sorular
    getExamContent() {
      let self = this;

      //  mevcut marker'ları temizle
      if (this.player && this.player.markers) {
        clearQuestionMarkers(this.player);
      }

      // Video daha önce izlenmişse sınav verisi alma
      let content_complete_status = this.item_content.unlocked_items.find(
          (x) => x === this.selected_content.id
      );

      if (content_complete_status) {
        showInfo("Bu video daha önce izlendiğinden, sınav soruları tekrar gösterilmeyecek.", 8000)
        self.isContentCompleted = true;
        self.questionsLoaded = true;

        // video izlnemiş ise sorular tamamlandı diye kabul edilebilir
        self.checkAndUpdateAnsweredQuestions();
        return;
      }

      let url = `api/assignment/${self.$route.params.id}/content/${this.selected_content.id}/exam`;

      self.$store.dispatch(GET_ITEM_DETAIL_BY_ID, {
        url: url,
        acceptPromise: false,
      })
          .then((result) => {
            if (result.status) {
              self.questions = result.data.questions || [];

              // Soru marker'larını ekle
              if (self.questions.length > 0 && self.player) {
                // Player hazır olduğunda marker'ları ekle
                if (self.player.readyState() >= 1) {
                  addQuestionMarkers(self.player, self.questions);
                } else {
                  // Player henüz hazır değilse, hazır olduğunda ekle
                  self.player.one('loadedmetadata', () => {
                    addQuestionMarkers(self.player, self.questions);
                  });
                }
              }
            }

            // Sorular yüklendi
            self.questionsLoaded = true;

            // Her iki API de tamamlandıysa answered questions'ı güncelle
            self.checkAndUpdateAnsweredQuestions();
          });
    },

    // parent de modal kapanınca ref ile buarsı tetiklenecek
    updateVideoWhereStay() {
      if (!this.player || !this.selected_content || !this.hashedCode) return false;

      const currentTime = this.player.currentTime();
      const duration = this.player.duration();

      if (!duration || isNaN(duration)) return false;

      const secondsDuration = Number(
          Math.min(duration, Math.max(0, currentTime)).toFixed(3)
      );
      // Uzun videolarda konum hassasiyetini korurken payload'ı 4 ondalık haneyle sınırla.
      const completionPercentage = Number(
          ((secondsDuration * 100) / duration).toFixed(4)
      );

      this.updateContentData = {
        completion_percentage: completionPercentage,
        playback_position_seconds: secondsDuration,
        hashed: this.hashedCode, // Hash değerini gönder
      };

      // Backend'e gönder
      this.$store.dispatch(UPDATE_ITEM_BY_ID, {
        url: `api/assignment/update-content/${this.$route.params.id}/${this.selected_content.id}`,
        contents: this.updateContentData,
        noLoading: true,
      }).then((result) => {
        // Yeni hash'i parent'a emit et
        if (result.status && result.data) {
          this.$emit('update-hash', result.data.hashed);
        }
      }).catch((error) => {
        showError(error);
      });
    },

    getVideoWhereIStay(content_id) {
      let self = this;
      let url = `api/assignment/${self.$route.params.id}/content/${content_id}/stay`;

      self.$store.dispatch(GET_ITEM_DETAIL_BY_ID, {
        url: url,
        acceptPromise: false,
      })
          .then((result) => {
            if (result.status) {
              // Stay endpoint'i kaldığı konumu saniye cinsinden döndürür.
              const timeInSeconds = Number(result.data);

              if (Number.isFinite(timeInSeconds) && timeInSeconds >= 0) {
                
                // Eğer saniye 1'den büyükse 0.2 saniye geriye al - amaç soru gösterimi kesinlikle olsun -
                if (timeInSeconds > 1) {
                  this.currentDuration = timeInSeconds - 0.2;
                } else {
                  this.currentDuration = timeInSeconds;
                }
              } else {
                this.currentDuration = 0;
              }
            } else {
              // Eğer pozisyon bulunamazsa loading'i kapat
              this.isLoading = false;
              if (this.player && this.player.el()) {
                this.player.el().classList.remove('vjs-loading');
              }
            }

            // Video pozisyonu yüklendi
            this.positionLoaded = true;

            // Her iki API de tamamlandıysa answered questions'ı güncelle
            this.checkAndUpdateAnsweredQuestions();
          })
          .catch((error) => {
            console.error('Video pozisyonu alınamadı:', error);
            this.isLoading = false;
            this.removeBigPlayButton()
          });
    },

    setVideoPosition(position) {
      if (!this.player || !this.player.duration()) {
        // Video henüz hazır değilse,tekrar dene
        setTimeout(() => {
          this.setVideoPosition(position);
        }, 500);
        return;
      }

      const duration = this.player.duration();

      // Eğer pozisyon video süresinden büyükse, baştan başlat
      if (position >= duration) {
        this.player.currentTime(0);
      } else {
        this.player.currentTime(position);
      }

      // Video pozisyonuna göre önceki soruları cevaplanmış kabul et
      this.updateAnsweredQuestionsBasedOnPosition(position);

      // Video pozisyonu ayarlandıktan sonra loading'i kapat
      setTimeout(() => {
        this.isLoading = false;
        this.removeBigPlayButton()
        // Video daha önce tamamlanmışsa ileri sarma aktif kalsın
        if (!this.isContentCompleted) {
          disableTimelineControls(this.player);
        }
      }, 600);
    },

    // Video pozisyonuna göre önceki soruları cevaplanmış , sonraki sorular cevaplanmamış kabul edilecek
    // Video position değişince de cevaplanmış sorular buna göre değişecek
    updateAnsweredQuestionsBasedOnPosition(currentPosition) {
      if (!this.questions || this.questions.length === 0) return;

      const currentTimeInSeconds = timeToSeconds(currentPosition);
      const newAnsweredQuestions = [];

      // Mevcut pozisyondan önceki tüm soruları cevaplanmış kabul edilecek
      this.questions.forEach((question) => {
        const questionTime = timeToSeconds(question.time_in_video);

        if (questionTime < currentTimeInSeconds) {
          newAnsweredQuestions.push(question);
        }
      });

      // Cevaplanmış soruları tamamen yeniden hesapla
      this.answeredQuestions = newAnsweredQuestions;

    },

    checkQuestionTiming(currentTime) {
      if (!this.questions || this.questions.length === 0) return;

      // Video süresini saniye cinsine çevir
      const timeInSeconds = timeToSeconds(currentTime);

      // Tüm soruları kontrol et
      this.questions.forEach((question) => {
        const questionTime = timeToSeconds(question.time_in_video);

        // Soru gösterilme koşulları:
        // 1. Video zamanı soru zamanına ulaştı (>=)
        // 2. Soru henüz cevaplanmamış (obje kontrolü)
        // 3. Şu anda başka bir soru gösterilmiyor
        const condition1 = timeInSeconds >= questionTime;
        const condition2 = !this.answeredQuestions.some(q => q.id === question.id);
        const condition3 = !this.currentQuestion;

        if (condition1 && condition2 && condition3) {
          this.showQuestion(question);
        } else {
          // soru gösterilmiyor
        }
      });
    },

    showQuestion(question) {
      this.currentQuestion = question;

      // Modal açıldıktan sonra fullscreen durumunu kontrol et
      this.$nextTick(() => {
        if (this.player && this.player.isFullscreen()) {
          this.handleFullscreenChange();
        }
      });
    },

    // soru cevaplandı -  videoya devam et
    continueToVideo() {
      if (this.apiResult) {
        // soru doğru ise ve back_time varsa, back_time'ı es geç
        if (this.isCorrectFlag(this.apiResult.is_correct)) {
          // Doğru cevap: Soruyu answeredQuestions'a ekle
          if (this.currentQuestion) {
            this.answeredQuestions.push(this.currentQuestion);
            this.player.play();
          }
        } else {
          if (this.apiResult.back_time) {
            // Back time varsa: Back time'ın geçerli olup olmadığını kontrol et
            const questionTime = timeToSeconds(this.currentQuestion.time_in_video);
            const backTime = timeToSeconds(this.apiResult.back_time);

            if (backTime < questionTime) {
              // Back time geçerli: Back time'a göre answeredQuestions güncelle
              this.seekToBackTime(this.apiResult.back_time);
            } else {
              // Back time geçersiz: Soruyu answeredQuestions'a ekle ve mevcut pozisyonda kal
              console.warn('Back time geçersiz: Back time soru zamanından büyük olamaz');
              if (this.currentQuestion) {
                this.answeredQuestions.push(this.currentQuestion);
                this.player.play();
              }
            }
          } else {
            // Back time yoksa: Soruyu answeredQuestions'a ekle
            if (this.currentQuestion) {
              this.answeredQuestions.push(this.currentQuestion);
              this.player.play();
            }
          }
        }
      }

      this.currentQuestion = null;
      this.apiResult = null;
      this.enableAllVideoControls(); // Tüm video kontrollerini tekrar aç
    },

    // soru cevaplama
    answerTheSelectedQuestion(questionId, optionId) {
      if (!optionId) {
        return;
      }

      const url = `api/assignment/${this.$route.params.id}/content/${this.selected_content.id}/exam/interaction-result`;

      const payload = {
        question_id: questionId,
        option_id: optionId
      };

      this.$store.dispatch(UPDATE_ITEM_BY_ID, {
        url: url,
        contents: payload,
        noLoading: true,
      }).then((result) => {
        if (result.status) {
          const apiData = result.data || {};
          const selectedOption = this.currentQuestion?.options?.find(
              (option) => String(option.id) === String(optionId)
          );

          // Backend is_correct alanını boolean/string/number döndürebilir veya
          // sonuç ayarlarına göre hiç döndürmeyebilir. GET exam cevabındaki
          // seçeneği fallback olarak kullanarak sonucu tek bir akışta tut.
          this.apiResult = {
            ...apiData,
            is_correct: apiData.is_correct ?? selectedOption?.is_correct
          };

          // QuestionFeedbackOverlay'e sonucu bildir - result modalını aç
          this.$nextTick(() => {
            if (this.$refs.questionOverlayRef) {
              this.$refs.questionOverlayRef.showResult = true;
            }
          });
        } else {
          let errorMessage = 'Bir hata oluştu';

          if (result.error && result.error.message && result.error.message.has_error) {
            if (result.error.message.message) {
              errorMessage = this.$t(`general.${result.error.message.message}`)
            }
          }

          showError(errorMessage);
        }
      }).catch(() => {
        showError(this.$t('errors.error_occurred'));
      });
    },

    isCorrectFlag(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },

    // Back time'a geri sar - eğer yanlış cevaplanırsa ve back_time degeri varsa
    seekToBackTime(backTime) {
      if (!this.player || !backTime) return;
      // Back time'ı saniye cinsine çevir
      const backTimeInSeconds = timeToSeconds(backTime);
      this.player.currentTime(backTimeInSeconds);

      // Video pozisyonu değiştikten sonra answeredQuestions'ı güncelle
      this.player.one('seeked', () => {
        this.currentVideoTime = this.player.currentTime();
        this.updateAnsweredQuestionsBasedOnPosition(backTime);
        this.player.play();
      });
    },

    // Her iki API de tamamlandıysa
    checkAndUpdateAnsweredQuestions() {
      if (this.questionsLoaded && this.positionLoaded) {
        // Video pozisyonunu ayarla
        this.setVideoPosition(this.currentDuration);
      } else {
        console.error("⏳ API'ler henüz tamamlanmadı, bekleniyor...");
      }
    },

    removeBigPlayButton() {
      // Video player'dan loading class'ını kaldır
      if (this.player && this.player.el()) {
        this.player.el().classList.remove('vjs-loading');
      }
    },

    pauseVideo() {
      if (this.player) {
        this.player.pause();
        this.disableAllVideoControls(); // Tüm video kontrollerini devre dışı bırak (Timeline alanı)
      }
    },

    playVideo() {
      if (this.player) {
        this.player.play();
      }
    },
    // Soru modalı açıldığında Tüm video timeline kontrollerini aktif-pasif yap
    disableAllVideoControls() {
      if (!this.player) return;

      this.player.controls(false);
      // big play button'u gizle
      this.player.el().classList.add('vjs-controls-disabled');
    },

    enableAllVideoControls() {
      if (!this.player) return;

      this.player.controls(true);
      this.player.el().classList.remove('vjs-controls-disabled');
    },

    // Fullscreen modda iken soru modalı video altında kalıyor
    handleFullscreenChange() {
      if (!this.player || !this.$refs.questionOverlayRef?.$el) return;

      const isFullscreen = this.player.isFullscreen();
      const questionOverlayEl = this.$refs.questionOverlayRef.$el;

      // Modal'ı doğru konuma taşı
      const targetContainer = isFullscreen ? this.player.el() : this.$el.querySelector('.video-container');

      if (targetContainer) {
        targetContainer.appendChild(questionOverlayEl);

        // CSS class ve stilleri ayarla
        questionOverlayEl.classList.toggle('fullscreen-overlay', isFullscreen);
        this.setModalVisibility(questionOverlayEl);
      }
    },

    // Modal görünürlüğünü ayarla
    setModalVisibility(modalElement) {
      modalElement.style.display = 'flex';
      modalElement.style.visibility = 'visible';
      modalElement.style.opacity = '1';
    },

  }
}
</script>

<style scoped src="./interactive-video-exam-player.css"/>
